import {
  Button,
  ColumnTable,
  Loading,
  Table,
  TableCollapsable,
} from '@octano/global-ui';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Col, Row } from 'reactstrap';
import { SectionTitle } from '../../../components/text';
import { TranslationsKeys } from '../../../locales/translations';
import { formatDate } from '../../../utils/dates';
import { StudentAction } from '../actions/studentActions';
import { useStudentActionsLoader } from './loaders/StudentActionsLoader';
import StudentActionCreateModal, {
  StudentActionCreateModalMethods,
} from './StudentActionCreateModal';
import StudentActionRecordCreateModal, {
  StudentActionRecordCreateModalMethods,
} from './StudentActionRecordCreateModal';

const StudentActionsTable = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_OBSERVATIONS,
  );

  const { result, loading, refresh } = useStudentActionsLoader();
  const data = result?.success ? result.data : null;

  const createActionRef = useRef<StudentActionCreateModalMethods>(null);
  const createRecordRef = useRef<StudentActionRecordCreateModalMethods>(null);

  const texts = useMemo(() => {
    return {
      title: t('studentActions.title'),
      session: t('studentActions.table.columns.session'),
      date: t('studentActions.table.columns.date'),
      groupType: t('studentActions.table.columns.group'),
      type: t('studentActions.table.columns.type'),
      notes: t('studentActions.table.columns.notes'),
      owner: t('studentActions.table.columns.owner'),
      adminAccount: t('studentActions.table.columns.adminAccount'),
      addAction: t('studentActions.addAction'),
      addUpdate: t('studentActions.table.actions.addUpdate'),
    };
  }, [t]);

  const columns = useMemo(() => {
    const cols: ColumnTable<StudentAction>[] = [
      {
        headerText: texts.session,
        columnName: 'session',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value?.name ?? '-';
        },
      },
      {
        headerText: texts.date,
        columnName: 'date',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return formatDate(row?.createdAt, 'DD/MM/YYYY') || '-';
        },
      },
      {
        headerText: texts.groupType,
        columnName: 'groupType',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return (
            data?.formOptions.warningGroupOptions.find(
              (option) => option.value === value,
            )?.label ??
            value ??
            '-'
          );
        },
      },
      {
        headerText: texts.type,
        columnName: 'actionType',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return (
            data?.formOptions.actionTypesOptions.find(
              (option) => option.value === value,
            )?.label ??
            value ??
            '-'
          );
        },
      },
      {
        headerText: texts.notes,
        columnName: 'notes',
        width: '35%',
        thClassName: 'text-center bg-secondary',
        cellFormat: ({ value }) => {
          if (!value) return '-';
          return (
            <div
              style={{
                maxWidth: '300px',
                textAlign: 'justify',
                padding: '8px',
                margin: '0 auto',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        headerText: texts.owner,
        columnName: 'owner',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.adminAccount,
        columnName: 'adminAccount',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value?.name ?? '-';
        },
      },
    ];
    return cols;
  }, [texts, data]);

  const subColumns = useMemo(() => {
    const cols: ColumnTable<any>[] = [
      {
        headerText: texts.date,
        columnName: 'date',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return formatDate(row?.createdAt, 'DD/MM/YYYY') || '-';
        },
      },
      {
        headerText: texts.notes,
        columnName: 'notes',
        width: '50%',
        thClassName: 'text-center',
        cellFormat: ({ value }) => {
          if (!value) return '-';
          return (
            <div
              style={{
                maxWidth: '400px',
                textAlign: 'justify',
                padding: '8px',
                margin: '0 auto',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        headerText: texts.adminAccount,
        columnName: 'adminAccount',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value?.name ?? '-';
        },
      },
    ];
    return cols;
  }, [texts]);

  if (loading) {
    return <Loading textBody={''} />;
  }

  if (!data) {
    return <></>;
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        <Row className="d-flex justify-content-between align-items-center mt-4">
          <Col xs={12} md={6}>
            <SectionTitle text={texts.title} />
          </Col>
          <Col
            xs={12}
            md={6}
            className="d-flex flex-row align-items-center justify-content-end"
          >
            <div style={{ maxWidth: 275 }}>
              <Button
                text={texts.addAction}
                size="md"
                outlined
                fullwidth
                type="button"
                icon="plus"
                onClick={() => createActionRef?.current?.open()}
              />
            </div>
          </Col>
          <Col className="mt-4" xs={12}>
            <TableCollapsable
              columns={columns}
              collapsableThProps={{
                className: 'bg-secondary',
              }}
              data={data.actions || []}
              isLoading={loading}
              secondColumn={{
                columnName: 'second',
                headerText: 'second',
                cellFormat: ({ row }) => (
                  <div className="w-100">
                    <Table
                      borderless={false}
                      data={row?.records || []}
                      color="transparent"
                      striped
                      columns={subColumns}
                    />
                    <div className="w-100 d-flex flex-row align-items-center justify-content-end mt-4">
                      <Button
                        text={texts.addUpdate}
                        size="md"
                        type="button"
                        onClick={() => createRecordRef?.current?.open(row.id)}
                      />
                    </div>
                  </div>
                ),
              }}
            />
          </Col>
        </Row>
      </div>

      <StudentActionCreateModal
        ref={createActionRef}
        studentId={studentId}
        groupTypeOptions={data.formOptions.warningGroupOptions || []}
        actionTypesOptions={data.formOptions.actionTypesOptions || []}
        onSave={refresh}
      />

      <StudentActionRecordCreateModal
        ref={createRecordRef}
        studentId={studentId}
        onSave={refresh}
      />
    </>
  );
};

export default StudentActionsTable;
